import React, { useCallback, useEffect, useState } from "react";
import { FiAlertTriangle, FiSettings, FiTrash2, FiMessageCircle, FiLink, FiLogOut } from "react-icons/fi";
import { useUserId } from "../UserIdContext";
import {
  CLEANUP_CONFIRMATION_PHRASE,
  conectarWhatsapp,
  desvincularWhatsapp,
  obtenerEstadoWhatsapp,
  limpiarOperativaBinder,
  resolveDepositoActual,
} from "./unidadesNegocioApi";
import "./usuarios.css";
import "./configuracion.css";

const binderLabel = (deposito) => (deposito === "binder2" ? "Binder 2" : "Binder 1");
const normalizeCleanupText = (value) =>
  String(value || "").trim().replace(/\s+/g, " ").toUpperCase();

const whatsappConnectionLabel = (status) => {
  switch (status) {
    case "ready":
      return "Cuenta vinculada";
    case "authenticated":
      return "Autenticando";
    case "qr":
      return "Esperando escaneo";
    case "initializing":
      return "Iniciando";
    case "refreshing_qr":
      return "Renovando QR";
    case "disconnected":
      return "Desconectada";
    case "auth_failure":
      return "Fallo de autenticacion";
    case "error":
      return "Error de conexion";
    default:
      return "Sin vincular";
  }
};

export default function ConfiguracionUnidades() {
  const { deposito, isAdmin } = useUserId();
  const depositoActual = resolveDepositoActual(deposito);
  const [cleanupConfirm, setCleanupConfirm] = useState("");
  const [cleanupSubmitting, setCleanupSubmitting] = useState(false);
  const [cleanupFeedback, setCleanupFeedback] = useState(null);

  const [whatsappStatus, setWhatsappStatus] = useState(null);
  const [whatsappStatusLoading, setWhatsappStatusLoading] = useState(true);
  const [whatsappActionLoading, setWhatsappActionLoading] = useState(false);
  const [whatsappFeedback, setWhatsappFeedback] = useState(null);

  const cargarEstadoWhatsapp = useCallback(async ({ silent = false } = {}) => {
    if (!isAdmin) return;
    if (!silent) setWhatsappStatusLoading(true);
    try {
      const data = await obtenerEstadoWhatsapp();
      setWhatsappStatus(data || null);
    } catch (error) {
      if (!silent) console.error("GET /api/configuracion/whatsapp", error?.response || error);
      setWhatsappStatus(null);
    } finally {
      if (!silent) setWhatsappStatusLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) {
      setWhatsappStatusLoading(false);
      return undefined;
    }

    cargarEstadoWhatsapp();
    const poll = window.setInterval(() => {
      cargarEstadoWhatsapp({ silent: true });
    }, 5000);

    return () => window.clearInterval(poll);
  }, [cargarEstadoWhatsapp, isAdmin]);

  const onConnectWhatsapp = async () => {
    try {
      setWhatsappActionLoading(true);
      setWhatsappFeedback(null);
      const data = await conectarWhatsapp();
      setWhatsappFeedback({ type: "ok", text: data?.message || "Iniciando WhatsApp Web." });
      window.setTimeout(() => cargarEstadoWhatsapp({ silent: true }), 1500);
    } catch (error) {
      setWhatsappFeedback({
        type: "error",
        text: error?.response?.data?.error || "No se pudo iniciar WhatsApp Web.",
      });
    } finally {
      setWhatsappActionLoading(false);
    }
  };

  const onDisconnectWhatsapp = async () => {
    if (!window.confirm("Desvincular la cuenta actual y generar un QR nuevo?")) return;
    try {
      setWhatsappActionLoading(true);
      setWhatsappFeedback(null);
      const data = await desvincularWhatsapp();
      setWhatsappFeedback({ type: "ok", text: data?.message || "Cuenta desvinculada." });
      window.setTimeout(() => cargarEstadoWhatsapp({ silent: true }), 1500);
    } catch (error) {
      setWhatsappFeedback({
        type: "error",
        text: error?.response?.data?.error || "No se pudo desvincular WhatsApp.",
      });
    } finally {
      setWhatsappActionLoading(false);
    }
  };

  const buildCleanupSummary = (data) => {
    const deleted = data?.deleted || {};
    return [
      `${deleted.ingresos || 0} ingresos`,
      `${deleted.pallets || 0} pallets`,
      `${deleted.egresos || 0} pedidos/egresos`,
      `${deleted.operaciones || 0} dias operativos`,
      `${deleted.productos || 0} productos huerfanos`,
      `${deleted.lotes || 0} lotes huerfanos`,
    ].join(", ");
  };

  const onCleanup = async () => {
    if (!isAdmin) {
      alert("Solo un usuario administrador puede ejecutar esta limpieza.");
      return;
    }

    if (
      normalizeCleanupText(cleanupConfirm) !== CLEANUP_CONFIRMATION_PHRASE
    ) {
      alert(`Escribe "${CLEANUP_CONFIRMATION_PHRASE}" para confirmar.`);
      return;
    }

    if (
      !window.confirm(
        `Se eliminaran ingresos, pallets, pedidos y operaciones diarias del ${binderLabel(
          depositoActual
        )}. Se conservaran clientes, usuarios, sucursales/factor de ocupacion y unidades de negocio.`
      )
    ) {
      return;
    }

    try {
      setCleanupSubmitting(true);
      setCleanupFeedback(null);
      const data = await limpiarOperativaBinder({
        deposito: depositoActual,
        confirmacion: cleanupConfirm,
      });
      setCleanupConfirm("");
      setCleanupFeedback({
        type: "ok",
        text: `Limpieza completada en ${binderLabel(depositoActual)}: ${buildCleanupSummary(
          data
        )}.`,
      });
    } catch (error) {
      console.error("Limpiar operativa", error?.response || error);
      const message =
        error?.response?.data?.error || "No se pudo limpiar la operativa.";
      setCleanupFeedback({
        type: "error",
        text: message,
      });
      alert(message);
    } finally {
      setCleanupSubmitting(false);
    }
  };

  return (
    <div className="config-wrap">
      <div className="users-header">
        <div className="title-stack">
          <h1><FiSettings /> Configuracion</h1>
          <p className="subtitle">Opciones del {binderLabel(depositoActual)}</p>
        </div>
      </div>
      <div className="config-grid">
          {isAdmin && <section className="panel config-panel config-whatsapp-panel">
            <div>
              <h2>
                <FiMessageCircle /> Verificacion por WhatsApp
              </h2>
              <p className="subtitle">
                Valida una vez cada dispositivo con un codigo de 6 digitos enviado al celular del
                usuario.
              </p>
            </div>

            <div className="config-meta">
              <span className={`pill ${whatsappStatus?.enabled ? "ok" : ""}`}>
                {whatsappStatusLoading
                  ? "Consultando..."
                  : whatsappStatus?.enabled
                    ? "Activa"
                    : "Desactivada"}
              </span>
              <span className={`pill ${whatsappStatus?.ready ? "ok" : ""}`}>
                {whatsappConnectionLabel(whatsappStatus?.connectionStatus)}
              </span>
            </div>

            {whatsappStatus && (
              <div className="config-whatsapp-details">
                <div>
                  <span>Proveedor</span>
                  <strong>{whatsappStatus.provider || "WhatsApp Web"}</strong>
                </div>
                <div>
                  <span>Cuenta</span>
                  <strong>
                    {whatsappStatus.ready
                      ? `Vinculada${whatsappStatus.accountLast4 ? ` (termina en ${whatsappStatus.accountLast4})` : ""}`
                      : "Sin vincular"}
                  </strong>
                </div>
                <div>
                  <span>Vigencia del codigo</span>
                  <strong>{Math.ceil((whatsappStatus.otpTtlSeconds || 300) / 60)} minutos</strong>
                </div>
                <div>
                  <span>Usuarios sin celular</span>
                  <strong>{whatsappStatus.usersWithoutPhone || 0}</strong>
                </div>
              </div>
            )}

            {whatsappStatus?.qrDataUrl && (
              <div className="config-whatsapp-qr">
                <img
                  key={whatsappStatus.qrGeneratedAt || whatsappStatus.qrDataUrl.slice(-24)}
                  src={whatsappStatus.qrDataUrl}
                  alt="Codigo QR para vincular WhatsApp"
                />
                <p>
                  En el celular abre WhatsApp, entra en <strong>Dispositivos vinculados</strong>,
                  pulsa <strong>Vincular un dispositivo</strong> y escanea este codigo. El QR se
                  renueva automaticamente cada {whatsappStatus.qrRefreshSeconds || 45} segundos.
                </p>
              </div>
            )}

            {whatsappStatus?.enabled && !whatsappStatus?.configured && (
              <div className="config-feedback error">
                Faltan variables del servidor: {(whatsappStatus.missing || []).join(", ")}.
              </div>
            )}

            {whatsappStatus?.lastError && (
              <div className="config-feedback error">{whatsappStatus.lastError}</div>
            )}

            {whatsappFeedback && (
              <div className={`config-feedback ${whatsappFeedback.type}`}>
                {whatsappFeedback.text}
              </div>
            )}

            <div className="config-actions">
              {!whatsappStatus?.ready && (
                <button
                  type="button"
                  className="btn solid"
                  onClick={onConnectWhatsapp}
                  disabled={whatsappActionLoading}
                >
                  <FiLink /> {whatsappStatus?.qrDataUrl ? "Renovar QR" : "Generar QR"}
                </button>
              )}
              {(whatsappStatus?.linked || whatsappStatus?.ready) && (
                <button
                  type="button"
                  className="btn ghost"
                  onClick={onDisconnectWhatsapp}
                  disabled={whatsappActionLoading}
                >
                  <FiLogOut /> Desvincular cuenta
                </button>
              )}
            </div>

            <div className="config-whatsapp-note">
              La cuenta se vincula por QR y la sesion queda guardada solo en el servidor de Binder.
              <code> WHATSAPP_ENABLED=true</code> activa el envio del codigo en el login; usa
              <code> false</code> para desactivarlo sin perder la vinculacion.
            </div>
          </section>}

          <section className="panel config-panel config-danger-panel">
            <div>
              <h2>
                <FiAlertTriangle /> Reiniciar operativa
              </h2>
              <p className="subtitle">
                Borra pedidos, ingresos, pallets y operaciones diarias del binder actual sin tocar
                clientes, usuarios, sucursales/factor de ocupacion ni unidades de negocio.
              </p>
            </div>

            <div className="config-meta">
              <span className="pill">{binderLabel(depositoActual)}</span>
              <span className="pill">Solo administradores</span>
            </div>

            <div className="field">
              <label>Escribe {CLEANUP_CONFIRMATION_PHRASE}</label>
              <input
                value={cleanupConfirm}
                onChange={(event) => setCleanupConfirm(event.target.value)}
                placeholder={CLEANUP_CONFIRMATION_PHRASE}
                disabled={cleanupSubmitting || !isAdmin}
              />
            </div>

            <div className="config-danger-note">
              La limpieza es por binder y no se puede deshacer.
            </div>

            {!isAdmin && (
              <div className="config-feedback error">
                Tu usuario no tiene permisos para ejecutar esta accion.
              </div>
            )}

            {cleanupFeedback?.text && (
              <div className={`config-feedback ${cleanupFeedback.type || ""}`}>
                {cleanupFeedback.text}
              </div>
            )}

            <div className="config-actions">
              <button
                type="button"
                className="btn danger"
                onClick={onCleanup}
                disabled={cleanupSubmitting || !isAdmin}
              >
                <FiTrash2 /> {cleanupSubmitting ? "Limpiando..." : "Eliminar operativa"}
              </button>
            </div>
          </section>
      </div>
    </div>
  );
}
